import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGroups } from '../../../store/actions/groupsActions';
import { fetchDashboards } from '../../../store/actions/dashsActions';
import { createUser } from '../../../store/actions/usersActions';
import TitlePage from '../../../components/general/TitlePage/TitlePage';
import Input from '../../../components/general/Input/Input';
import TitleSectionComponent from '../../../components/intern/TitleSectionComponent/TitleSectionComponent';
import CheckBoxContainer from '../../../components/general/CheckBox/CheckBoxContainer';
import RadioGroupComponent from "../../../components/RadioGroup/RadioGroup";
import { USER_ROLES } from "../../../constants/userRoles";

import Button from '../../../components/general/Button/Button';
import TableDashboardForm from '../../../components/intern/TableInform/TableDashboardForm';
import AlertComponent from '../../../components/intern/Alert/Alert';
import './UserSearchList.css';

const CriarUsuarioPage = () => {
    const dispatch = useDispatch();
    const [selectedRole, setSelectedRole] = useState("client");
    const [dashboardName, setDashboardName] = useState("");
    const [filteredDashboards, setFilteredDashboards] = useState([]);
    const [selectedDashboards, setSelectedDashboards] = useState([]);
    const [userName, setUserName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [userPassword, setUserPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [userNameError, setUserNameError] = useState("");
    const [userEmailError, setUserEmailError] = useState("");
    const [userPasswordError, setUserPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");
    const { groups, loadingGroups, errorGroups } = useSelector((state) => state.groups);
    const { dashs, loadingDashs, errorDashs } = useSelector((state) => state.dashs);

    const navigate = useNavigate();

    const [alert, setAlert] = useState(null);
    const alertTimeoutId = useRef(null);

    const showAlert = (type, message) => {
        // Limpa timeout anterior
        if (alertTimeoutId.current) {
            clearTimeout(alertTimeoutId.current);
        }
        setAlert({ type, message });

        alertTimeoutId.current = setTimeout(() => {
            setAlert(null);
            alertTimeoutId.current = null;
        }, 5000);
    };

    useEffect(() => {
        return () => {
            if (alertTimeoutId.current) clearTimeout(alertTimeoutId.current);
        };
    }, []);


    useEffect(() => {
        dispatch(fetchGroups());
        dispatch(fetchDashboards());


    }, [dispatch]);

    const handleGroupSelection = (selectedGroups) => setSelectedGroups(selectedGroups);

    const handleUserNameChange = (e) => {
        const value = e.target.value;
        setUserName(value);

        // Verificar se o nome tem pelo menos 3 caracteres e se não contém números ou caracteres especiais
        const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/;
        if (value.length < 3) {
            setUserNameError("O nome completo precisa ter no mínimo 3 caracteres.");
        } else if (!nameRegex.test(value)) {
            setUserNameError("O nome completo não pode conter números ou caracteres especiais.");
        } else {
            setUserNameError("");
        }
    };

    const handleUserEmailChange = (e) => {
        const value = e.target.value;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Regex para validar o formato do e-mail

        setUserEmail(value);

        if (value.length > 255) {
            setUserEmailError("E-mail muito longo! O limite é 255 caracteres.");
        } else if (!emailRegex.test(value) && value.length > 0) {
            setUserEmailError("Formato de e-mail inválido! Certifique-se de que tenha o formato correto (ex: usuario@dominio.com).");
        } else {
            setUserEmailError("");
        }
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setUserPassword(value);

        // Verificações de segurança de senha
        const minLength = 8;
        const hasUpperCase = /[A-Z]/.test(value);
        const hasNumber = /\d/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

        if (value.length < minLength) {
            setUserPasswordError("A senha precisa ter pelo menos 8 caracteres.");
        } else if (!hasUpperCase) {
            setUserPasswordError("A senha precisa ter pelo menos uma letra maiúscula.");
        } else if (!hasNumber) {
            setUserPasswordError("A senha precisa ter pelo menos um número.");
        } else if (!hasSpecialChar) {
            setUserPasswordError("A senha precisa ter pelo menos um caractere especial (ex: @,#,$,...).");
        } else {
            setUserPasswordError("");
        }
    };

    const handleConfirmPasswordChange = (e) => {
        const value = e.target.value;
        setConfirmPassword(value);

        if (value !== userPassword) {
            setConfirmPasswordError("As senhas não coincidem.");
        } else {
            setConfirmPasswordError("");
        }
    };

    const handleRoleChange = (roleValue) => {
        setIsAdmin(roleValue);
    };

    const handleDashboardNameChange = (e) => {
        const value = e.target.value;
        setDashboardName(value);

        if (value.length > 0) {
            const matches = dashs.filter(
                (dashboard) =>
                    dashboard.dashboardName.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredDashboards(matches);
        } else {
            setFilteredDashboards([]);
        }
    };

    const handleAddDashboard = (dashboard) => {
        if (selectedDashboards.some(dash => dash.id === dashboard.id)) {
            showAlert('excluir', 'Dashboard já adicionado!');
        } else {
            setSelectedDashboards([...selectedDashboards, dashboard]);
        }

    };

    const handleRemoveDashboard = (dashboardId) => {
        setSelectedDashboards(selectedDashboards.filter((dash) => dash.id !== dashboardId));
        showAlert('excluir', 'Dashboard removido com sucesso.');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (userNameError || userEmailError || userPasswordError || confirmPasswordError || !userName || !userEmail || !userPassword || !confirmPassword) {
            showAlert('excluir', 'Preencha todos os campos obrigatórios corretamente!');
            return;
        }

        try {
            let response;
            let approved = true;
            const selectedDashboardsFilter = (selectedDashboards || []).map(dash => dash.id);
            // Criação de novo usuário
            response = await dispatch(
                createUser({ userName, userEmail, userPassword, isAdmin, approved, selectedRole, selectedDashboardsFilter, selectedGroups })
            ).unwrap();

            showAlert('adicionar');

            // Limpar campos após salvar (somente para criação, não para edição)
            setUserEmail("");
            setUserPassword("");
            setConfirmPassword("");
            setSelectedDashboards([]);
            setSelectedGroups([]);
            setUserNameError("");
            setUserEmailError("");
            setUserPasswordError("");
            setConfirmPasswordError("");

            setUserName("");

            navigate("/dashboard");
        } catch (error) {
            // Se vier do backend uma mensagem clara de e-mail duplicado
            if (
                error?.message?.toLowerCase().includes("e-mail já cadastrado") ||
                error?.message?.toLowerCase().includes("email já cadastrado") ||
                error?.message?.toLowerCase().includes("já existe um usuário com este e-mail")
            ) {
                setUserEmailError("Este e-mail já está cadastrado!");
            }
            showAlert('excluir', error.message || 'Erro ao criar usuário');
        }
    };

    if (loadingGroups || loadingDashs) return <p>Carregando sistema... Por favor, aguarde.</p>;
    if (errorGroups || errorDashs) return <p>Erro: {errorGroups || errorDashs}</p>;

    return (
        <section className="container-extern content-with-sidebar">
            <TitlePage
                title="Novo Usuário"
                subtitle="Cadastre um novo usuário e configure as permissões de acesso de forma precisa e segura."
            />

            <Helmet>
                <title>New User</title>
            </Helmet>

            <form onSubmit={handleSubmit}>
                <Input
                    type="text"
                    placeholder="Digite aqui o nome completo do usuário ..."
                    label="Nome Completo:"
                    value={userName}
                    onChange={handleUserNameChange}
                    errorMessage={userNameError}
                />

                <Input
                    type="text"
                    placeholder="Digite aqui o e-mail de acesso do usuário..."
                    label="E-mail do Usuário:"
                    value={userEmail}
                    onChange={handleUserEmailChange}
                    errorMessage={userEmailError}
                />

                <Input
                    type="password"
                    placeholder="Digite aqui a senha..."
                    label="Senha:"
                    value={userPassword}
                    onChange={handlePasswordChange}
                    errorMessage={userPasswordError}
                />

                <Input
                    type="password"
                    placeholder="Digite aqui a senha..."
                    label="Confirmar senha:"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    errorMessage={confirmPasswordError}
                />

                <RadioGroupComponent
                    title="Tipo de Usuário:"
                    name="role"
                    options={USER_ROLES}
                    value={selectedRole}
                    onChange={(val) => {
                        setSelectedRole(val);
                        setIsAdmin(val !== "client");
                    }}
                />

                <TitleSectionComponent>Permitir acesso aos dashboards existentes:</TitleSectionComponent>
                <Input
                    type="text"
                    placeholder="Digite aqui nome do dashboard cadastrado"
                    label="Nome do Dashboard:"
                    value={dashboardName}
                    onChange={handleDashboardNameChange}
                />

                {filteredDashboards.length > 0 ? (
                    <ul className="user-search-list common-list-style">
                        {filteredDashboards.map((dash) => (
                            <li
                                key={dash.id || dash.dashboardName}
                                onClick={() => handleAddDashboard(dash)}
                                className="user-search-item"
                            >
                                {dash.dashboardName}
                            </li>
                        ))}
                    </ul>
                ) : (
                    dashboardName && <p className="no-results common-list-style">Nenhum dashboard encontrado com esse nome. Tente novamente.</p>
                )}

                <TableDashboardForm
                    dados={selectedDashboards}
                    tipoContato="Dashboard"
                    exibirAcesso={false}
                    exibirEditar={false}
                    onExcluir={dashboard => handleRemoveDashboard(dashboard.id)}
                    onEditar={() => { }}
                />

                <TitleSectionComponent>Permitir acesso aos grupos existentes:</TitleSectionComponent>

                <CheckBoxContainer
                    options={groups.map(group => ({ label: group.groupName, value: group.uid }))}
                    onChange={handleGroupSelection}
                    initialValues={selectedGroups}
                />

                <div className="section-btn-page">
                    <Button
                        type="submit"
                        className='btn-add'
                        variant="add"
                        disabled={userNameError || userEmailError || userPasswordError || confirmPasswordError || !userName || !userEmail || !userPassword || !confirmPassword}>
                        Criar Usuário
                    </Button>
                </div>
                {alert && <AlertComponent type={alert.type} onClose={() => setAlert(null)} />}
            </form>
        </section>
    );
};

export default CriarUsuarioPage;
